import { ComponentType, PropsWithChildren, ReactElement, useMemo } from 'react';

import { useSelector } from 'react-redux';
import { RouterActionsType } from 'react-resource-router';

import { FocusedPageHeader } from 'src/components/accessibility';
import { useMenuItems } from 'src/hooks/use-container-navigation-props';
import { getIsMobileHeaderActive } from 'src/selectors/global-selectors';

import { MenuItem } from '../navigation';

import { SettingsPageSection, SettingsPageWrapper } from './settings.styled';

const MOBILE_NAV_HEIGHT = 54;

export type SharedProps = {
  selectedMenuItem?: MenuItem | undefined;
  topOffset?: number;
  pageTitle?: string;
  url?: string;
  push?: RouterActionsType['push'];
};

export type Props = {
  header?: ComponentType<PropsWithChildren<SharedProps>>;
  actions?: ComponentType<PropsWithChildren<unknown>> | ReactElement;
  breadcrumbs?: ComponentType<PropsWithChildren<SharedProps>>;
  container?: ComponentType<PropsWithChildren<unknown>>;
  noLayout?: boolean;
  passHeader?: boolean;
  url?: string;
  push?: RouterActionsType['push'];
};

const createSettingsHOC =
  (defaultProps?: Props) =>
  <P,>(
    Component: ComponentType<PropsWithChildren<P & Props & SharedProps>>,
    componentProps?: Props
  ): ComponentType<PropsWithChildren<P & Props>> => {
    return settingsProps => {
      const isMobileHeaderActive = useSelector(getIsMobileHeaderActive);
      const topOffset = isMobileHeaderActive ? MOBILE_NAV_HEIGHT : 0;
      const { selectedMenuItem } = useMenuItems();
      const props = { ...defaultProps, ...componentProps, ...settingsProps };
      const { noLayout } = props;
      const sharedProps = useMemo<SharedProps>(
        () => ({ selectedMenuItem, topOffset }),
        [selectedMenuItem, topOffset]
      );

      if (noLayout) {
        return <Component {...props} {...sharedProps} />;
      }

      const {
        actions: Actions,
        header: Header,
        container: Container,
        breadcrumbs: PageBreadcrumbs,
        passHeader,
      } = props as P & Props;

      const PageHeader = useMemo<ComponentType<SharedProps>>(
        () =>
          (headerProps: Partial<SharedProps> = {}) =>
            Header && typeof Header === 'function' ? (
              <Header {...sharedProps} {...headerProps} />
            ) : (
              <FocusedPageHeader
                actions={typeof Actions === 'function' ? <Actions /> : Actions}
                breadcrumbs={
                  PageBreadcrumbs ? (
                    <PageBreadcrumbs {...sharedProps} {...headerProps} />
                  ) : undefined
                }
              >
                {headerProps.pageTitle ||
                  sharedProps.pageTitle ||
                  sharedProps.selectedMenuItem?.label}
              </FocusedPageHeader>
            ),
        [Header, Actions, PageBreadcrumbs, sharedProps]
      );

      const layout = (
        <SettingsPageSection>
          <SettingsPageWrapper>
            {passHeader ? null : <PageHeader />}
            <Component
              {...props}
              {...sharedProps}
              header={passHeader ? PageHeader : undefined}
            />
          </SettingsPageWrapper>
        </SettingsPageSection>
      );

      if (Container) {
        return <Container>{layout}</Container>;
      }

      return layout;
    };
  };

// eslint-disable-next-line import/no-default-export
export default createSettingsHOC;
